import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Plus, Edit, Trash2, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import DataGrid from '../components/grid/DataGrid';
import Modal from '../components/ui/Modal';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { apiService } from '../services/api';
import { EntryType, CreateEntryTypeDto, UpdateEntryTypeDto } from '../types';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import ConfirmDialog from '../components/modals/ConfirmDialog';

const schema = yup.object({
  name: yup.string().required('Name is required').max(50, 'Name must be less than 50 characters'),
  description: yup.string().max(200, 'Description must be less than 200 characters'),
  displayOrder: yup.number().required('Display order is required').min(0, 'Display order must be 0 or greater'),
  isActive: yup.boolean().required()
});

type FormData = yup.InferType<typeof schema>;

interface EntryTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: FormData) => void;
  isLoading: boolean;
  entryType?: EntryType | null;
}

const EntryTypeModal: React.FC<EntryTypeModalProps> = ({ isOpen, onClose, onSubmit, isLoading, entryType }) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: entryType?.name || '',
      description: entryType?.description || '',
      displayOrder: entryType?.displayOrder || 0,
      isActive: entryType?.isActive ?? true
    }
  });

  React.useEffect(() => {
    if (entryType) {
      reset({
        name: entryType.name,
        description: entryType.description || '',
        displayOrder: entryType.displayOrder,
        isActive: entryType.isActive
      });
    } else {
      reset({
        name: '',
        description: '',
        displayOrder: 0,
        isActive: true
      });
    }
  }, [entryType, reset]);

  const handleFormSubmit = (data: FormData) => {
    onSubmit(data);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={entryType ? 'Edit Entry Type' : 'Create Entry Type'} maxWidth="md">
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
        {/* Active checkbox at the top */}
        <div className="flex items-center space-x-2 pb-4 border-b border-gray-200">
          <Controller
            name="isActive"
            control={control}
            render={({ field }) => (
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            )}
          />
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Active
          </label>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                placeholder="Enter entry type name"
                className={errors.name ? 'border-red-500' : ''}
              />
            )}
          />
          {errors.name && (
            <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Description
          </label>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                placeholder="Enter entry type description"
                className={errors.description ? 'border-red-500' : ''}
              />
            )}
          />
          {errors.description && (
            <p className="text-red-500 text-sm mt-1">{errors.description.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Display Order <span className="text-red-500">*</span>
          </label>
          <Controller
            name="displayOrder"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                type="number"
                min="0"
                placeholder="Enter display order"
                className={errors.displayOrder ? 'border-red-500' : ''}
                onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
              />
            )}
          />
          {errors.displayOrder && (
            <p className="text-red-500 text-sm mt-1">{errors.displayOrder.message}</p>
          )}
        </div>

        <div className="flex justify-end space-x-2 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Saving...' : entryType ? 'Update' : 'Create'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

const EntryTypesAdminPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<string>('displayOrder');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingEntryType, setEditingEntryType] = useState<EntryType | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [entryTypeToDelete, setEntryTypeToDelete] = useState<EntryType | null>(null);

  const { data: entryTypesResponse, isLoading, error } = useQuery({
    queryKey: ['entryTypes'],
    queryFn: () => apiService.getEntryTypes(),
  });

  const entryTypes = useMemo(() => {
    let all = entryTypesResponse?.data || [];
    
    // Apply search filter
    if (search.trim()) {
      const q = search.toLowerCase();
      all = all.filter((et: EntryType) =>
        et.name.toLowerCase().includes(q) ||
        (et.description || '').toLowerCase().includes(q)
      );
    }
    
    // Apply sorting
    all = [...all].sort((a, b) => {
      const aValue = (a as any)[sortField];
      const bValue = (b as any)[sortField];
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' 
          ? aValue - bValue
          : bValue - aValue;
      }
      
      if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        return sortDirection === 'asc' 
          ? (aValue === bValue ? 0 : aValue ? 1 : -1)
          : (aValue === bValue ? 0 : aValue ? -1 : 1);
      }
      
      return 0;
    });
    
    return all;
  }, [entryTypesResponse, search, sortField, sortDirection]);

  // Calculate pagination
  const totalCount = entryTypes.length;
  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedEntryTypes = entryTypes.slice(startIndex, endIndex);

  const createMutation = useMutation({
    mutationFn: (data: CreateEntryTypeDto) => apiService.createEntryType(data),
    onSuccess: () => {
      toast.success('Entry type created successfully!');
      queryClient.invalidateQueries({ queryKey: ['entryTypes'] });
      setIsCreateModalOpen(false);
    },
    onError: (err: any) => {
      toast.error(`Failed to create entry type: ${err.message || 'Unknown error'}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateEntryTypeDto }) => apiService.updateEntryType(id, data),
    onSuccess: () => {
      toast.success('Entry type updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['entryTypes'] });
      setIsEditModalOpen(false);
      setEditingEntryType(null);
    },
    onError: (err: any) => {
      toast.error(`Failed to update entry type: ${err.message || 'Unknown error'}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiService.deleteEntryType(id),
    onSuccess: () => {
      toast.success('Entry type deleted successfully!');
      queryClient.invalidateQueries({ queryKey: ['entryTypes'] });
      setIsDeleteConfirmOpen(false);
      setEntryTypeToDelete(null);
    },
    onError: (err: any) => {
      toast.error(`Failed to delete entry type: ${err.message || 'Unknown error'}`);
    },
  });

  const handleDeleteClick = (entryType: EntryType) => {
    setEntryTypeToDelete(entryType);
    setIsDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (entryTypeToDelete) {
      deleteMutation.mutate(entryTypeToDelete.entryTypeId);
    }
  };

  // Pagination handlers
  const handleSearch = (term: string) => {
    setSearch(term);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const columns = useMemo(() => [
    {
      id: 'name',
      header: 'Name',
      sortable: true,
      cell: (value: any, row: EntryType) => (
        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{row.name}</div>
      )
    },
    {
      id: 'description',
      header: 'Description',
      sortable: false,
      cell: (value: any, row: EntryType) => (
        <div className="text-sm text-gray-700 dark:text-gray-300">{row.description || '-'}</div>
      )
    },
    {
      id: 'displayOrder',
      header: 'Display Order',
      sortable: true,
      cell: (value: any, row: EntryType) => (
        <div className="text-sm text-gray-900 dark:text-gray-100">{row.displayOrder}</div>
      )
    },
    {
      id: 'isActive',
      header: 'Active',
      sortable: true,
      cell: (value: any, row: EntryType) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          row.isActive 
            ? 'bg-green-100 text-green-800' 
            : 'bg-red-100 text-red-800'
        }`}>
          {row.isActive ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      id: 'createdDate',
      header: 'Created Date',
      sortable: true,
      cell: (value: any, row: EntryType) => (
        <div className="relative group">
          <div className="group-hover:opacity-0 transition-opacity duration-200">
            <div className="text-sm text-gray-900 dark:text-gray-100">
              {new Date(row.createdDate).toLocaleDateString()}
            </div>
          </div>
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-end gap-2 pr-2" onClick={(e)=>e.stopPropagation()}>
            <button 
              onClick={() => {
                setEditingEntryType(row);
                setIsEditModalOpen(true);
              }}
              className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
              title="Edit Entry Type"
            >
              <Edit className="h-4 w-4"/>
            </button>
            <button 
              onClick={() => handleDeleteClick(row)}
              className="h-7 px-2 inline-flex items-center border rounded-md text-red-600 border-red-300 hover:bg-red-50"
              title="Delete Entry Type"
            >
              <Trash2 className="h-4 w-4"/>
            </button>
          </div>
        </div>
      )
    }
  ], []);

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Entry Type Management</h1>
        </div>
        <div className="bg-white shadow rounded-lg p-6">
          <div className="text-center">
            <div className="text-red-600 text-lg font-medium">Error loading entry types</div>
            <div className="text-gray-500 mt-2">Please try again</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <MessageSquare className="h-8 w-8 text-gray-600" />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Entry Type Management</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Manage timeline entry types</p>
          </div>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Entry Type
        </Button>
      </div>

      <DataGrid
        data={paginatedEntryTypes}
        columns={columns}
        isLoading={isLoading}
        searchTerm={search}
        onSearch={handleSearch}
        currentPage={currentPage}
        pageSize={pageSize}
        totalCount={totalCount}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onSort={handleSort}
        sortField={sortField}
        sortDirection={sortDirection}
        searchPlaceholder="Search entry types..."
        emptyMessage="No entry types found"
        getRowClassName={() => 'group'}
        onRowClick={(row) => {
          setEditingEntryType(row as EntryType);
          setIsEditModalOpen(true);
        }}
        rowTooltip="Edit Entry Type"
      />

      {/* Create Modal */}
      <EntryTypeModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={(data) => createMutation.mutate(data)}
        isLoading={createMutation.isPending}
      />

      {/* Edit Modal */}
      <EntryTypeModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingEntryType(null);
        }}
        onSubmit={(data) => editingEntryType && updateMutation.mutate({ id: editingEntryType.entryTypeId, data })}
        isLoading={updateMutation.isPending}
        entryType={editingEntryType}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Entry Type"
        message={`Are you sure you want to delete "${entryTypeToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        isLoading={deleteMutation.isPending}
        variant="danger"
      />
    </div>
  );
};

export default EntryTypesAdminPage;
